import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { buildWeek, buildSession, swap, describeSession } from '../services/planService';
import { RefreshCw, Play } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import exercisesData from '../data/exercises.json';
import ExerciseInfoModal from '../components/ExerciseInfoModal';
import toast from 'react-hot-toast';

/**
 * SessionOverview (PR-07)
 *
 * Day overview screen before the session starts.
 * Shows full exercise list, allows swaps, and has a Start button.
 */
function SessionOverview() {
  const { day } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showExerciseInfo, setShowExerciseInfo] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userPrefs = userDoc.data().prefs || {};
          setPrefs(userPrefs);

          let sessionPlan;

          if (day === 'quick') {
            const tier = parseInt(searchParams.get('tier')) || 2;
            const minutes = parseInt(searchParams.get('minutes')) || 30;
            const focus = searchParams.get('focus') || 'full-body';

            sessionPlan = buildSession(exercisesData, {
              tier,
              minutes,
              focus,
              seed: `quick-${Date.now()}`,
              excludedIds: userPrefs.excludedExerciseIds || [],
            });
          } else {
            const plan = buildWeek(exercisesData, {
              tier: userPrefs.defaultTier ?? 1,
              minutes: userPrefs.defaultDurationMin ?? 45,
              daysPerWeek: userPrefs.daysPerWeek ?? 3,
              seed: 'wk',
              excludedIds: userPrefs.excludedExerciseIds || [],
            });

            const dayIndex = parseInt(day);
            if (dayIndex >= 0 && dayIndex < plan.length) {
              sessionPlan = plan[dayIndex];
            }
          }

          if (sessionPlan) {
            setSession(sessionPlan);
          }
        }
      } catch (error) {
        console.error('Error loading session:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [user, day, searchParams]);

  const handleSwap = async (exerciseIndex) => {
    if (!session || !prefs) return;

    const currentExercise = session.exercises[exerciseIndex];
    const swapped = swap(exercisesData, currentExercise.exerciseId, prefs.defaultTier);

    if (swapped) {
      const newExercises = [...session.exercises];
      newExercises[exerciseIndex] = {
        exerciseId: swapped.id,
        name: swapped.name,
        sets: swapped.defaultSets,
        reps: swapped.defaultReps,
        restSeconds: swapped.defaultRestSeconds,
        movementPattern: swapped.movementPattern,
        muscleGroup: swapped.muscleGroup,
        minTier: swapped.minTier,
        substitutes: swapped.substitutes,
      };
      setSession({ ...session, exercises: newExercises });
      toast.success(`Swapped to ${swapped.name}`);
    } else {
      toast.error('No swap available');
    }
  };

  const handleStartWorkout = () => {
    // Navigate to actual session with same params
    navigate(`/session/${day}${day === 'quick' ? '?' + searchParams.toString() : ''}`);
  };

  const handleViewExerciseInfo = (exerciseId) => {
    const exercise = exercisesData.find(e => e.id === exerciseId);
    setSelectedExercise(exercise);
    setShowExerciseInfo(true);
  };

  const handleVideoUpdate = (exerciseId, youtubeUrl) => {
    // Update the selected exercise with the new video
    if (selectedExercise && selectedExercise.id === exerciseId) {
      setSelectedExercise({
        ...selectedExercise,
        media: {
          ...(selectedExercise.media || {}),
          youtubeUrl: youtubeUrl,
          userContributed: true,
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-lg text-light-ink dark:text-dark-ink font-plex">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-light-muted dark:text-dark-muted font-plex">Session not found</div>
      </div>
    );
  }

  const description = describeSession(session, exercisesData);
  const totalSets = session.exercises.reduce((sum, ex) => sum + ex.sets, 0);

  return (
    <div className="gymbo-app-shell bg-light-bg dark:bg-dark-bg">
      <div className="gymbo-scroll-area">
        {/* Header */}
        <div className="bg-light-surface dark:bg-dark-surface border-b border-light-border dark:border-dark-border px-4 py-6">
          <div className="text-[13px] font-plex font-medium text-light-muted dark:text-dark-muted uppercase tracking-[0.09em] mb-2">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
          <h1 className="text-[30px] font-grotesk font-bold text-light-ink dark:text-dark-ink tracking-[-0.02em] capitalize">
            {session.focus.replace('-', ' ')} Day
          </h1>
          <div className="mt-2 text-[13px] font-plex text-light-muted dark:text-dark-muted">
            {session.exercises.length} exercises · {totalSets} sets · {session.minutes} min
          </div>
        </div>

        {/* Description */}
        <div className="px-4 py-4">
          <div className="bg-light-accent-soft dark:bg-dark-accent-soft rounded-[16px] p-4">
            <p className="text-[15px] font-plex text-light-ink dark:text-dark-ink leading-relaxed">
              {description}
            </p>
          </div>
        </div>

        {/* Exercise List */}
        <div className="px-4 pb-4 space-y-2">
        {session.exercises.map((exercise, idx) => {
          const exerciseDetails = exercisesData.find(e => e.id === exercise.exerciseId);
          return (
            <div
              key={idx}
              className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-[16px] p-4"
            >
              <div className="flex items-start gap-3">
                {/* Exercise Image */}
                {exerciseDetails?.images?.[0] && (
                  <img
                    src={exerciseDetails.images[0]}
                    alt={exercise.name}
                    className="w-20 h-20 rounded-[12px] object-cover flex-shrink-0"
                  />
                )}

                {/* Exercise Details */}
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => handleViewExerciseInfo(exercise.exerciseId)}
                    className="text-left w-full"
                  >
                    <h3 className="text-[17px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-1 hover:text-light-accent dark:hover:text-dark-accent transition-colors">
                      {exercise.name}
                    </h3>
                  </button>
                  <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                    {exercise.sets} × {exercise.reps}
                    {exercise.restSeconds && ` · ${exercise.restSeconds}s rest`}
                  </div>
                </div>

                {/* Swap Button */}
                <button
                  onClick={() => handleSwap(idx)}
                  className="w-10 h-10 flex items-center justify-center rounded-[12px] bg-light-surface-2 dark:bg-dark-surface-2 hover:bg-light-border dark:hover:bg-dark-border transition-colors flex-shrink-0"
                >
                  <RefreshCw size={18} className="text-light-ink dark:text-dark-ink" />
                </button>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Start Button (Sticky above tab bar) */}
      <div className="gymbo-cta bg-light-surface dark:bg-dark-surface border-t border-light-border dark:border-dark-border p-4 shadow-lg">
        <button
          onClick={handleStartWorkout}
          className="w-full h-[58px] bg-light-accent dark:bg-dark-accent text-white rounded-[17px] font-plex font-semibold text-[17px] flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
        >
          <Play size={20} />
          Start Workout
        </button>
      </div>

      {/* Exercise Info Modal */}
      <ExerciseInfoModal
        exercise={selectedExercise}
        isOpen={showExerciseInfo}
        onClose={() => setShowExerciseInfo(false)}
        onVideoUpdate={handleVideoUpdate}
      />
    </div>
  );
}

export default SessionOverview;
